import { useMsal } from '@azure/msal-react';
import type { SupportedLocale } from '@brightdrop/localization-core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  createCdiAccountRequest,
  loginRequest,
  URL_CLEANUP_DELAY_MS,
} from '~/common/configs/auth.config';
import { AppRoutePaths } from '~/common/models/route.model';
import { getB2cLocaleQueryParameters } from '~/common/utils/auth/auth.utils';
import {
  AuthenticationType,
  type AuthType,
} from '~/models/authentication.model';

type LoginHintParams = {
  domainHint?: string;
  loginHint?: string;
};

export type UseLoginRedirectResult = {
  /** True from the moment a redirect is requested until the browser leaves the page. */
  formSubmitted: boolean;
  clearFormSubmitted: () => void;
  handleSubmit: (loginHint?: string) => void;
  handleCreateAccount: () => void;
};

/**
 * Owns the MSAL redirect handoff for the login page: sign-in, CDI account
 * creation, and the re-entry that follows a return from account creation.
 * @param redirectPath - Post-login destination carried through the redirect, if any
 * @param authenticationType - Auth type the MSAL instance was built for; picks the locale parameter B2C reads
 * @param locale - Locale sent to B2C as its display language
 */
const useLoginRedirect = (
  redirectPath: string | null,
  authenticationType: AuthType,
  locale: SupportedLocale
): UseLoginRedirectResult => {
  const { instance } = useMsal();
  const navigate = useNavigate();

  const [formSubmitted, setFormSubmitted] = useState(false);

  /** A return from account creation is waiting on the URL cleanup to commit. */
  const reentryPendingRef = useRef(false);

  const clearFormSubmitted = useCallback(() => setFormSubmitted(false), []);

  const handleLoginRedirect = useCallback(
    (params?: LoginHintParams) => {
      const redirectStartUrl = new URL(
        AppRoutePaths.ROOT,
        window.location.origin
      );
      if (redirectPath) {
        redirectStartUrl.searchParams.set('redirectPath', redirectPath);
      }
      const redirectStartPage = `${redirectStartUrl.pathname}${redirectStartUrl.search}`;
      const extraQueryParameters = {
        ...loginRequest.extraQueryParameters,
        ...getB2cLocaleQueryParameters(authenticationType, locale),
      };

      setFormSubmitted(true);
      instance
        .loginRedirect({
          ...loginRequest,
          ...params,
          extraQueryParameters,
          redirectStartPage,
          state: redirectStartUrl.toString(),
        })
        .catch((e) => {
          console.error('Login error - loginRedirect', e);
          navigate(AppRoutePaths.ROOT);
        });
    },
    [navigate, instance, redirectPath, authenticationType, locale]
  );

  const handleSubmit = useCallback(
    (loginHint?: string) => {
      const domainHint = loginHint?.split('@')?.[1];

      handleLoginRedirect({ loginHint, domainHint });
    },
    [handleLoginRedirect]
  );

  const handleCreateAccount = useCallback(() => {
    const redirectStartUrl = new URL(
      AppRoutePaths.LOGIN,
      window.location.origin
    );
    if (redirectPath) {
      redirectStartUrl.searchParams.set('redirectPath', redirectPath);
    }
    const redirectStartPage = `${redirectStartUrl.pathname}${redirectStartUrl.search}`;

    setFormSubmitted(true);

    instance
      .loginRedirect({
        ...createCdiAccountRequest,
        extraQueryParameters: {
          ...createCdiAccountRequest.extraQueryParameters,
          ...getB2cLocaleQueryParameters(AuthenticationType.CDI, locale),
        },
        redirectStartPage,
        state: redirectStartUrl.toString(),
        onRedirectNavigate: (url: string) => {
          try {
            const generatedUrl = new URL(url);
            const baseCreateAccountUrl =
              globalThis.appConfig.auth.cdiCreateAccountUrl;

            let createAccountUrl: URL;
            try {
              createAccountUrl = new URL(baseCreateAccountUrl);
            } catch (urlError) {
              console.error(
                'Failed to create URL from:',
                baseCreateAccountUrl,
                urlError
              );
              return true;
            }

            const msalParameters = [
              'nonce',
              'state',
              'prompt',
              'code_challenge',
              'code_challenge_method',
              'scope',
              'channel',
              'brand',
              'ui_locales',
            ];

            createAccountUrl.searchParams.set(
              'client_id',
              globalThis.appConfig.auth.gmFleetClientId
            );
            createAccountUrl.searchParams.set(
              'redirect_uri',
              window.location.origin
            );
            createAccountUrl.searchParams.set(
              'originDomain',
              `${window.location.origin}${AppRoutePaths.LOGIN}`
            );

            for (const param of msalParameters) {
              const value = generatedUrl.searchParams.get(param);
              if (value) {
                createAccountUrl.searchParams.set(param, value);
              }
            }

            const finalUrl = createAccountUrl.toString();

            window.location.assign(finalUrl);
          } catch (error) {
            console.error('Error constructing create account URL:', error);
            return true;
          }

          return false;
        },
      })
      .catch((e) => {
        console.error('Login error - loginRedirect for create account', e);
        window.location.reload();
      });
  }, [instance, redirectPath, locale]);

  /* Clean up URL parameters after successful account creation */
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    // Check for OAuth parameters that indicate return from account creation
    const hasOAuthParams =
      urlParams.has('client_id') ||
      urlParams.has('nonce') ||
      urlParams.has('state') ||
      urlParams.has('code_challenge');

    if (hasOAuthParams) {
      // Clean up URL parameters before calling handleLoginRedirect to avoid endpoint resolution conflicts
      const cleanUrl = new URL(
        window.location.pathname,
        window.location.origin
      );
      if (redirectPath) {
        cleanUrl.searchParams.set('redirectPath', redirectPath);
      }
      /*
       * The re-entry is detected from parameters this cleanup erases, so the
       * pending hand-off is tracked outside the URL. A later run of this
       * effect sees no OAuth parameters, and without the flag it could not
       * reschedule a hand-off that its own cleanup had cancelled.
       */
      reentryPendingRef.current = true;
      navigate(`${cleanUrl.pathname}${cleanUrl.search}`, { replace: true });
    }

    if (!reentryPendingRef.current) {
      return;
    }

    const reentryTimer = setTimeout(() => {
      reentryPendingRef.current = false;
      handleLoginRedirect();
    }, URL_CLEANUP_DELAY_MS);

    return () => clearTimeout(reentryTimer);
  }, [handleLoginRedirect, navigate, redirectPath]);

  return {
    formSubmitted,
    clearFormSubmitted,
    handleSubmit,
    handleCreateAccount,
  };
};

export default useLoginRedirect;
